import React from "react";
import { ChatToolType } from "_graphql-types-frontend/graphql";
import {
  ChatMessage,
  ToolCallEntry,
  MarkdownWithToolsMessage,
  ToolsReactMessage,
} from "../ChatBox/types";
import { mergeToolCalls } from "./toolMerge";
import { ToolsBlock } from "./toolDisplays";
import { MutationPlanCardConnected } from "../MutationPlan/MutationPlanCardConnected";
import { speakerMessages } from "./agentSpeakers";
import { ANSWER_MODEL_ID } from "./AnswerModel";

/**
 * Takes the prior chat history and a subscription payload, returns the next
 * chat history with tool calls merged, the tools display block built, the
 * markdown answer appended, and inline MutationPlanCards added for any
 * plan-producing tools.
 *
 * Pure function (the JSX it embeds is data; not React state). Called from
 * SlotWatcher's onData — the same logic runs whether the slot is active or
 * background, so a chat keeps streaming into its history while the user is
 * elsewhere. Calling it twice with the same payload is idempotent because
 * messages with stable IDs (messageId, stableToolsId, mutation-plan-N) are
 * replaced in place.
 */
export function applyStreamEventToHistory(
  history: ChatMessage[],
  data: any
): ChatMessage[] {
  // The request can return before the stream's last event, and a rebuilt
  // message moves to the end: keep the turn's model line below it.
  const last = history[history.length - 1];
  if (last?.id?.startsWith(ANSWER_MODEL_ID)) {
    return [...applyStreamEventToHistory(history.slice(0, -1), data), last];
  }
  const event = data.data?.rangerChatSubscription.messageStream.event;
  if (event === "start") return history;
  const isToolEvent = event === "toolStart" || event === "toolCall";
  const messageId = data.data?.rangerChatSubscription.messageStream.id;
  // Strip the toolAttempts segment from messageStream.id so retries reuse the
  // same tools block: id format is "sessionId-toolAttempts-questionUUID".
  const idStr = messageId ?? "";
  const firstDash = idStr.indexOf("-");
  const secondDash = firstDash >= 0 ? idStr.indexOf("-", firstDash + 1) : -1;
  const stableToolsId =
    secondDash >= 0
      ? `${idStr.slice(0, firstDash)}-${idStr.slice(secondDash + 1)}-tools`
      : `${idStr}-tools`;

  const indexById = new Map<string, number>();
  for (let i = 0; i < history.length; i++) {
    const id = history[i].id;
    if (id) indexById.set(id, i);
  }

  const existingMessageIdx = messageId ? indexById.get(messageId) : undefined;
  const existingMessage =
    existingMessageIdx !== undefined ? history[existingMessageIdx] : undefined;
  const existingTools: ToolCallEntry[] =
    (existingMessage?.data as MarkdownWithToolsMessage)?.tools || [];

  const existingToolsIdx = indexById.get(stableToolsId);
  const existingToolsMessage =
    existingToolsIdx !== undefined ? history[existingToolsIdx] : undefined;
  const accumulatedTools: ToolCallEntry[] =
    (existingToolsMessage?.data as ToolsReactMessage)?.allTools || [];

  const newTools = data.data?.rangerChatSubscription.tools ?? [];
  const currentTools = newTools.length > 0 ? newTools : existingTools;
  const mergedTools = mergeToolCalls(accumulatedTools, currentTools);

  // Build result by copying non-replaced messages in place — preserves
  // ordering and avoids a full filter+spread per event.
  const idsToReplace = new Set<string>();
  if (messageId) idsToReplace.add(messageId);
  idsToReplace.add(stableToolsId);

  // Persistent agents' messages are rebuilt from the spawns on every event,
  // like the tools block they come from.
  const speakerPrefix = `${stableToolsId}-speaker-`;
  const result: ChatMessage[] = [];
  for (let i = 0; i < history.length; i++) {
    const id = history[i].id;
    if (id && (idsToReplace.has(id) || id.startsWith(speakerPrefix))) continue;
    result.push(history[i]);
  }

  const isSubAgentSpawn = (t: ToolCallEntry) =>
    t.tool === ChatToolType.SpawnSubAgentTool && !t.toolName;
  const isSubAgentInternalTool = (t: ToolCallEntry) =>
    t.tool === ChatToolType.SpawnSubAgentTool && !!t.toolName;

  // Stream internal sub-agent tool calls into their parent spawn's results
  // so the collapsed sub-agent view shows them live, not just after the
  // spawn finishes.
  const internalTools = mergedTools.filter(isSubAgentInternalTool);
  const subAgentCalls = mergedTools.filter(isSubAgentSpawn).map(spawn => {
    if (spawn.results?.tools?.length > 0) return spawn;
    if (internalTools.length === 0) return spawn;
    if (!spawn.results) {
      return {
        ...spawn,
        results: {
          tools: internalTools.map(t => ({
            tool: t.tool,
            toolName: t.toolName,
            params: t.params,
            results: t.results,
          })),
        },
      };
    }
    return spawn;
  });
  const displayTools = mergedTools.filter(
    t =>
      t.tool !== ChatToolType.ChartTool &&
      !isSubAgentSpawn(t) &&
      !isSubAgentInternalTool(t)
  );

  if (mergedTools.length > 0) {
    // `displayTools ∪ subAgentCalls` is pre-filtered to exclude ChartTool
    // (rendered via inline {{chart:}} markers in the markdown body) and
    // internal sub-agent tool calls (merged into their parent spawn's
    // results above). ToolsBlock partitions what's left into regular tools
    // vs sub-agent spawns and returns null when both buckets are empty.
    result.push({
      type: "agent",
      id: stableToolsId,
      data: {
        type: "react",
        message: <ToolsBlock tools={[...displayTools, ...subAgentCalls]} />,
        allTools: mergedTools,
      } as ToolsReactMessage,
    });
  } else if (existingToolsMessage) {
    result.push(existingToolsMessage);
  }

  // After the tool card, before the assistant's own answer: the agent spoke
  // first, and the assistant responds to it.
  result.push(...speakerMessages(subAgentCalls, stableToolsId));

  // toolStart/toolCall events don't carry answer text — skip the markdown
  // message for those so we don't overwrite the prior answer with empty.
  if (!isToolEvent) {
    // Text events carry only the new text in messageStream.message; the
    // stop event carries the complete answer in `answer` and replaces the
    // accumulated text, so a dropped delta cannot leave the transcript short.
    const existingText =
      (existingMessage?.data as MarkdownWithToolsMessage | undefined)?.type ===
      "markdown"
        ? (existingMessage!.data as MarkdownWithToolsMessage).message || ""
        : "";
    const fullAnswer = data.data?.rangerChatSubscription.answer || "";
    const delta = data.data?.rangerChatSubscription.messageStream.message || "";
    const text = event === "stop" ? fullAnswer || existingText : existingText + delta;
    result.push({
      type: "agent",
      id: messageId,
      data: {
        type: "markdown",
        message: text,
        tools: mergedTools,
      } as MarkdownWithToolsMessage,
    });
  }

  // Inline MutationPlanCard for any tool that produced a planId. Plan IDs
  // are tracked in result/indexById so the same card isn't appended twice
  // when a later event includes the same tool call.
  const mutationPlanTools = mergedTools.filter(
    t =>
      t.results?.planId &&
      ((t.tool === ChatToolType.SiriusMcp &&
        t.toolName === "stageMutationPlan") ||
        t.tool === ChatToolType.StageMutationsTool ||
        t.tool === ChatToolType.ModifyMutationPlanTool ||
        (t.tool === ChatToolType.SpawnSubAgentTool &&
          t.toolName === "StageMutationsTool"))
  );
  for (const mpt of mutationPlanTools) {
    const cardId = `mutation-plan-${mpt.results.planId}`;
    if (!indexById.has(cardId) && !result.find(x => x.id === cardId)) {
      result.push({
        type: "agent",
        id: cardId,
        data: {
          type: "react",
          message: (
            <div data-mutation-plan-id={mpt.results.planId}>
              <MutationPlanCardConnected planId={mpt.results.planId} />
            </div>
          ),
        },
      });
    }
  }

  return result;
}
